import { useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * useClickBounds hook
 * @typedef {Object} useClickBoundsOptions
 * @param {Object|Array} ref - React Ref [or an array of Refs] to listen for event
 * @param {Function} callback - callback function to execute on event
 * @param {Boolean} outOfBounds - detect event inside / outside of ref
 * @param {String} onEvent - event to listen to
 * @param {Object} options - event options e.g. { capture: true }
 */
export const useClickBounds = ({ ref, callback, outOfBounds = true, onEvent = 'mousedown', options }) => {
  useEffect(() => {
    const checkBounds = (currentRef, event) => {
      if (!currentRef?.current) return false;
      const target = currentRef.current.contains(event.target);
      return outOfBounds ? !target : target;
    };
    const handleClick = event => {
      const refsToCheck = ref?.current ? [ref] : ref.length ? ref : [];
      const bounds = refsToCheck?.every(r => checkBounds(r, event));
      if (bounds) callback(event);
    };
    document.addEventListener(onEvent, handleClick, options);
    return () => {
      document.removeEventListener(onEvent, handleClick, options);
    };
  }, [ref, callback, outOfBounds, onEvent, options]);
};

useClickBounds.propTypes = {
  ref: PropTypes.object.isRequired,
  callback: PropTypes.func.isRequired,
  outOfBounds: PropTypes.bool,
  onEvent: PropTypes.string,
  options: PropTypes.object,
};
